import React from "react";

import { InfoOutlined } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  CircularProgress,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import {
  VehicleOption,
  useVehicleMakeOptions,
  useVehicleModelOptions,
} from "@/components/alerts/add-v2/hooks/useOptionLists";
import {
  VEHICLE_COLOURS,
  VEHICLE_OTHER,
  VEHICLE_TYPES,
  applyVehicleColour,
  applyVehicleMake,
  applyVehicleModel,
  applyVehicleType,
  vehicleModelIsForcedOther,
} from "@/components/alerts/add-v2/model/entityRules";
import { VehicleEntity } from "@/components/alerts/add-v2/model/types";

import {
  ENTITY_FULL_ROW_SX,
  ENTITY_PAIR_HALF_SX,
  ENTITY_PAIR_ROW_SX,
} from "./entityFieldLayout";

/**
 * Field order is the design's: Make, Model, Colour, Vehicle type, Licence plate, Jurisdiction,
 * Additional description. Involvement sits above these, in the editor.
 *
 * **Make and model are closed lists**, backed by NHTSA vPIC through `/public/vehicle-makes`. Every
 * stored value is a vPIC name or one of two sentinels, which is what keeps the search facets clean;
 * the free text behind `Other` goes to `makeOther` / `modelOther`. The two rules that tie them
 * together — a make change clears the model, a sentinel make forces the model to `Other` — live in
 * `model/entityRules.ts` so phase 3's schema can agree with them.
 *
 * **Colour and vehicle type are closed too**, over the design's own ten-value lists — a constant in
 * `model/entityRules.ts` rather than an endpoint, because there is nothing to administer. They
 * store a key and render through `t`, which is the one way they differ from make and model.
 *
 * All four pair a field with the free text behind its `Other`, side by side in one grid cell. The
 * layout is the prototype's `pairRowStyle`; see `entityFieldLayout.ts`.
 */

interface VehicleFieldsProps {
  value: VehicleEntity;
  onPatch: (next: Partial<VehicleEntity>) => void;
}

/** The design caps the plate at 10 characters. Partial plates are expected — see the tooltip. */
const PLATE_MAX_LENGTH = 10;

export const VehicleFields: React.FC<VehicleFieldsProps> = ({
  value,
  onPatch,
}) => {
  const { t } = useTranslation("bulletin/create-v2");
  const makes = useVehicleMakeOptions();
  const models = useVehicleModelOptions(value.make);

  const modelForcedOther = vehicleModelIsForcedOther(value);

  /**
   * The selected option, or a bare one built from the stored value. Both matter: in edit mode the
   * list has not loaded yet, and the field must still show what the bulletin holds rather than
   * appearing empty.
   */
  const selected = (
    options: VehicleOption[],
    key: string,
  ): VehicleOption | null => {
    if (!key) return null;
    return (
      options.find((option) => option.key === key) ?? {
        key,
        name: key,
        group: "all",
      }
    );
  };

  return (
    <>
      {/* Make and its free text share one cell, so picking `Other` does not move model off this row. */}
      <Box sx={ENTITY_PAIR_ROW_SX}>
        <Autocomplete
          size="small"
          fullWidth
          sx={ENTITY_PAIR_HALF_SX}
          autoComplete
          autoHighlight
          openOnFocus
          options={makes.options}
          loading={makes.loading}
          value={selected(makes.options, value.make)}
          groupBy={(option) => t(`entities.group_${option.group}`)}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, chosen) => option.key === chosen.key}
          onChange={(_event, option) =>
            onPatch(applyVehicleMake(value, option?.key ?? ""))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              label={t("entities.make")}
              slotProps={{
                input: {
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {makes.loading && (
                        <CircularProgress color="inherit" size={16} />
                      )}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                },
              }}
            />
          )}
        />
        {value.make === VEHICLE_OTHER && (
          <TextField
            size="small"
            fullWidth
            required
            sx={ENTITY_PAIR_HALF_SX}
            label={t("entities.make_other")}
            value={value.makeOther}
            onChange={(event) => onPatch({ makeOther: event.target.value })}
          />
        )}
      </Box>

      <Box sx={ENTITY_PAIR_ROW_SX}>
        {/* A sentinel make leaves no model list to pick from, so the field collapses to its free text. */}
        {!modelForcedOther && (
          <Autocomplete
            size="small"
            fullWidth
            sx={ENTITY_PAIR_HALF_SX}
            autoComplete
            autoHighlight
            openOnFocus
            disabled={!value.make}
            options={models.options}
            loading={models.loading}
            value={selected(models.options, value.model)}
            groupBy={(option) => t(`entities.group_${option.group}`)}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, chosen) => option.key === chosen.key}
            onChange={(_event, option) =>
              onPatch(applyVehicleModel(value, option?.key ?? ""))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label={
                  value.make
                    ? t("entities.model")
                    : t("entities.model_pick_make")
                }
                slotProps={{
                  input: {
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {models.loading && (
                          <CircularProgress color="inherit" size={16} />
                        )}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  },
                }}
              />
            )}
          />
        )}
        {(modelForcedOther || value.model === VEHICLE_OTHER) && (
          <TextField
            size="small"
            fullWidth
            sx={ENTITY_PAIR_HALF_SX}
            label={t("entities.model_other")}
            helperText={
              modelForcedOther ? t("entities.model_other_hint") : undefined
            }
            value={value.modelOther}
            onChange={(event) => onPatch({ modelOther: event.target.value })}
          />
        )}
      </Box>

      <Box sx={ENTITY_PAIR_ROW_SX}>
        <TextField
          select
          size="small"
          fullWidth
          sx={ENTITY_PAIR_HALF_SX}
          label={t("entities.colour")}
          value={value.colour}
          onChange={(event) =>
            onPatch(applyVehicleColour(value, event.target.value))
          }
        >
          {VEHICLE_COLOURS.map((option) => (
            <MenuItem key={option} value={option}>
              {t(`entities.colour_${option}`)}
            </MenuItem>
          ))}
        </TextField>
        {value.colour === VEHICLE_OTHER && (
          <TextField
            size="small"
            fullWidth
            sx={ENTITY_PAIR_HALF_SX}
            label={t("entities.colour_other_specify")}
            value={value.colourOther}
            onChange={(event) => onPatch({ colourOther: event.target.value })}
          />
        )}
      </Box>

      <Box sx={ENTITY_PAIR_ROW_SX}>
        <TextField
          select
          size="small"
          fullWidth
          sx={ENTITY_PAIR_HALF_SX}
          label={t("entities.vehicle_type")}
          value={value.vehicleType}
          onChange={(event) =>
            onPatch(applyVehicleType(value, event.target.value))
          }
        >
          {VEHICLE_TYPES.map((option) => (
            <MenuItem key={option} value={option}>
              {t(`entities.vehicle_type_${option}`)}
            </MenuItem>
          ))}
        </TextField>
        {value.vehicleType === VEHICLE_OTHER && (
          <TextField
            size="small"
            fullWidth
            sx={ENTITY_PAIR_HALF_SX}
            label={t("entities.vehicle_type_other_specify")}
            value={value.vehicleTypeOther}
            onChange={(event) =>
              onPatch({ vehicleTypeOther: event.target.value })
            }
          />
        )}
      </Box>

      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
        <TextField
          size="small"
          fullWidth
          label={t("entities.licence_plate")}
          placeholder={t("entities.licence_plate_placeholder")}
          helperText={t("entities.licence_plate_hint")}
          value={value.licencePlate}
          slotProps={{ htmlInput: { maxLength: PLATE_MAX_LENGTH } }}
          onChange={(event) => onPatch({ licencePlate: event.target.value })}
        />
        {/*
          The icon alone could not carry this: an MUI SVG sets `focusable="false"`, so a keyboard
          never reached the tooltip and the hint was mouse-only.
        */}
        <Tooltip title={t("entities.licence_plate_info")}>
          <IconButton
            size="small"
            aria-label={t("entities.licence_plate_info")}
            sx={{ mt: 0.75, flexShrink: 0 }}
          >
            <InfoOutlined color="info" fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <TextField
        size="small"
        fullWidth
        label={t("entities.jurisdiction")}
        value={value.jurisdiction}
        onChange={(event) => onPatch({ jurisdiction: event.target.value })}
      />

      <TextField
        size="small"
        fullWidth
        multiline
        minRows={3}
        sx={ENTITY_FULL_ROW_SX}
        label={t("entities.additional_description")}
        placeholder={t("entities.additional_description_vehicle_placeholder")}
        value={value.additionalDescription}
        onChange={(event) =>
          onPatch({ additionalDescription: event.target.value })
        }
      />
    </>
  );
};
